import { useState, useMemo, useEffect, useCallback } from 'react';
import _ from 'lodash';

/**
 * Custom hook to throttle high-frequency data updates for React Data Grids.
 * Prevents React render storms when Redux arrays update rapidly (e.g., via WebSockets).
 * 
 * @param {Array} computedData The raw data array from the Redux selector
 * @param {Number} wait Delay in milliseconds (default 700)
 * @param {Function} onChangeCallback (Optional) Callback fired with only the objects that changed since last throttle
 * @param {String} idProperty (Optional) The primary key to diff objects, e.g "id"
 * @param {Array} propertiesToValidate (Optional) The keys to check for mutations (if empty, checks whole object)
 * @param {Array} trackedIds (Optional) Array of tracked IDs. If provided, the UI will ONLY re-render if one of these IDs explicitly changed.
 * @returns {Array} Throttled data array safe for UI rendering
 */
export const useDataGridThrottler = (computedData, wait = 700, onChangeCallback = null, idProperty = "id", propertiesToValidate = [], trackedIds = null) => {
    const [data, setData] = useState([]);

    const trackedIdsStr = trackedIds ? JSON.stringify(trackedIds) : null;

    const throttledSetData = useMemo(() => _.throttle((newData) => {
        setData(prevData => {
            let shouldUpdateState = true;
            const parsedTrackedIds = trackedIdsStr ? JSON.parse(trackedIdsStr) : null;

            // If a callback is provided OR we need to filter updates by trackedIds, compute O(N) delta diff before setting the new UI state
            if ((onChangeCallback || (parsedTrackedIds && parsedTrackedIds.length > 0)) && newData && newData.length > 0) {
                const prevDataMap = new Map();
                if (prevData && prevData.length > 0) {
                    prevData.forEach(item => {
                        const key = item[idProperty];
                        if (key !== undefined) {
                            prevDataMap.set(String(key), item);
                        }
                    });
                }

                const changesQueue = [];
                const changedUnits = [];

                newData.forEach(newItem => {
                    const key = newItem[idProperty];
                    if (key === undefined) return;

                    const oldItem = prevDataMap.get(String(key));

                    if (!oldItem) {
                        changedUnits.push(newItem);
                        return;
                    }

                    let hasChanges = false;

                    if (propertiesToValidate.length > 0) {
                        propertiesToValidate.forEach(p => {
                            if (!_.isEqual(oldItem[p], newItem[p])) {
                                changesQueue.push({
                                    id: key,
                                    atribute: p,
                                    oldValue: oldItem[p],
                                    newValue: newItem[p]
                                });
                                hasChanges = true;
                            }
                        });
                    } else {
                        if (!_.isEqual(oldItem, newItem)) {
                            hasChanges = true;
                        }
                    }

                    if (hasChanges) {
                        changedUnits.push(newItem);
                    }
                });

                if (onChangeCallback && (changedUnits.length > 0 || changesQueue.length > 0)) {
                    onChangeCallback({ data: changedUnits, changes: changesQueue });
                }

                if (parsedTrackedIds && parsedTrackedIds.length > 0) {
                    const trackedChanged = changedUnits.some(item => {
                        const key = item[idProperty];
                        return parsedTrackedIds.includes(String(key)) || parsedTrackedIds.includes(key);
                    });

                    if (!trackedChanged) {
                        shouldUpdateState = false;
                    }
                }
            }

            return shouldUpdateState ? newData : prevData;
        });
    }, wait), [wait, onChangeCallback, idProperty, propertiesToValidate, trackedIdsStr]);

    useEffect(() => {
        if (computedData) {
            throttledSetData(computedData);
        }
        // Cancel the throttle on unmount to prevent memory leaks or state updates on unmounted components
        return () => throttledSetData.cancel();
    }, [computedData, throttledSetData]);

    return data;
};

/**
 * Advanced version of useDataGridThrottler that internally manages trackedIds state.
 * Returns the throttled data array and a callback to update the tracked IDs.
 * 
 * @returns {[Array, Function]} [data, changeIdsCallback]
 */
export const useDataGridThrottlerWithTrackedIds = (computedData, wait = 700, onChangeCallback = null, idProperty = "id", propertiesToValidate = []) => {
    const [trackedIds, setTrackedIds] = useState(null);

    const data = useDataGridThrottler(computedData, wait, onChangeCallback, idProperty, propertiesToValidate, trackedIds);

    const changeIdsCallback = useCallback((value, field = "id") => {
        if (!value) {
            setTrackedIds(null);
            return;
        }
        let idsN = value?.filter(Boolean).map(x => x[field]?.toString());
        setTrackedIds(prev => _.isEqual(prev, idsN) ? prev : idsN);
    }, []);

    return [data, changeIdsCallback];
};

export default useDataGridThrottler;
